import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Target, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Eye,
  Loader2,
  Users,
  TrendingUp,
  Zap,
  Filter,
  Building,
  ArrowLeft,
  Pencil,
  Trash2,
  AlertTriangle,
  FileText,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { LeadFormModal } from "../../components/admin/LeadFormModal";

const API = "/api";

// Stat Card Component
const StatCard = ({ icon: Icon, value, label, color = "#14A800", small = false }) => (
  <div className={`bg-[#121212] rounded-xl border border-white/5 ${small ? 'p-4' : 'p-6'}`}>
    <div className="flex items-center gap-3">
      <div className={`${small ? 'p-2' : 'p-3'} rounded-lg`} style={{ backgroundColor: `${color}20` }}>
        <Icon className={`${small ? 'w-4 h-4' : 'w-5 h-5'}`} style={{ color }} />
      </div>
      <div>
        <p className={`${small ? 'text-xl' : 'text-2xl'} font-bold text-white font-[Manrope]`}>{value}</p>
        <p className="text-xs text-zinc-500">{label}</p>
      </div>
    </div>
  </div>
);

// Stage Badge Component
const StageBadge = ({ stage }) => {
  const styles = {
    "Lead": { bg: "bg-blue-500/10", text: "text-blue-400", icon: "🎯" },
    "Opportunity": { bg: "bg-[#14A800]/10", text: "text-[#14A800]", icon: "🔥" },
    "Implementation": { bg: "bg-purple-500/10", text: "text-purple-400", icon: "🚀" },
  };
  const style = styles[stage] || styles["Lead"];
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md ${style.bg} ${style.text}`}>
      {style.icon} {stage}
    </span>
  );
};

// Status Badge Component
const StatusBadge = ({ status, stage }) => {
  const getStatusColor = (status) => {
    const colors = {
      // Lead statuses
      "New": "bg-blue-500/10 text-blue-400",
      "Attempted Contact": "bg-orange-500/10 text-orange-400",
      "Contacted": "bg-purple-500/10 text-purple-400",
      "Nurturing": "bg-cyan-500/10 text-cyan-400",
      "Qualified": "bg-green-500/10 text-green-400",
      "Unqualified": "bg-red-500/10 text-red-400",
      "Converted": "bg-[#14A800]/10 text-[#14A800]",
      // Opportunity statuses
      "Qualify": "bg-blue-500/10 text-blue-400",
      "Discovery": "bg-cyan-500/10 text-cyan-400",
      "Solution Design": "bg-amber-500/10 text-amber-400",
      "Negotiate": "bg-orange-500/10 text-orange-400",
      "Contracting": "bg-[#14A800]/10 text-[#14A800]",
      "Review": "bg-indigo-500/10 text-indigo-400",
      "Closed Won": "bg-emerald-500/10 text-emerald-400",
      "Closed Lost": "bg-zinc-500/10 text-zinc-400",
      // Implementation statuses
      "Not Started": "bg-slate-500/10 text-slate-400",
      "In Progress": "bg-blue-500/10 text-blue-400",
      "QA": "bg-purple-500/10 text-purple-400",
      "UAT": "bg-amber-500/10 text-amber-400",
      "Blocked": "bg-red-500/10 text-red-400",
      "Completed": "bg-emerald-500/10 text-emerald-400",
      "Cancelled": "bg-zinc-500/10 text-zinc-400",
    };
    return colors[status] || "bg-zinc-500/10 text-zinc-400";
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-md ${getStatusColor(status)}`}>
      {status}
    </span>
  );
};

// Source Badge Component
const SourceBadge = ({ source }) => {
  const isInbound = source === "inbound";
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-md ${
      isInbound ? "bg-green-500/10 text-green-400" : "bg-blue-500/10 text-blue-400"
    }`}>
      {isInbound ? "↘ Inbound" : "↗ Outbound"}
    </span>
  );
};

// Priority Score Badge
const PriorityBadge = ({ score }) => {
  const getColor = (score) => {
    if (score >= 80) return "bg-red-500/10 text-red-400 border-red-500/20";
    if (score >= 60) return "bg-orange-500/10 text-orange-400 border-orange-500/20";
    if (score >= 40) return "bg-yellow-500/10 text-yellow-400 border-yellow-500/20";
    return "bg-zinc-500/10 text-zinc-400 border-zinc-500/20";
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-bold rounded-md border ${getColor(score)}`}>
      {score}
    </span>
  );
};

export default function AdminLeads() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pipelineStats, setPipelineStats] = useState({});
  
  // Lead Form Modal state (for both create and edit)
  const [showLeadFormModal, setShowLeadFormModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [leadFormMode, setLeadFormMode] = useState("create");
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem("admin_token");
  const userRole = localStorage.getItem("admin_role") || "admin";
  const isProductManager = userRole === "product_manager";
  const isSales = userRole === "sales";
  const canSeeContractsStage = userRole === "admin" || userRole === "product_manager";

  // Pipeline stages based on role
  const getPipelineStages = () => {
    if (isSales) {
      return ["Lead"];  // Sales only sees Lead stage
    } else if (isProductManager) {
      return ["Opportunity", "Contracts & Payments", "Implementation"];
    } else if (canSeeContractsStage) {
      return ["Lead", "Opportunity", "Contracts & Payments", "Implementation"];
    }
    return ["Lead", "Opportunity", "Implementation"];
  };
  const PIPELINE_STAGES = getPipelineStages();

  // Stage-specific statuses
  const STAGE_STATUSES = {
    "Lead": ["New", "Attempted Contact", "Contacted", "Nurturing", "Qualified", "Unqualified", "Converted"],
    "Opportunity": ["Qualify", "Discovery", "Solution Design", "Negotiate", "Contracting", "Review", "Closed Won", "Closed Lost"],
    "Contracts & Payments": [
      "Not Started",
      "Contract Drafting",
      "Contract Sent",
      "Contract Signed – Pending Invoice",
      "Invoice Sent",
      "Payment Pending",
      "Initial Payment Received",
      "Final Payment Received",
      "Payment Failed / Returned",
      "On Hold",
      "Cancelled"
    ],
    "Implementation": ["Not Started", "In Progress", "QA", "UAT", "Blocked", "Completed", "Cancelled"]
  };

  // Get statuses based on selected stage
  const getAvailableStatuses = () => {
    if (stageFilter === "all") {
      if (isSales) {
        // Sales only sees Lead statuses
        return STAGE_STATUSES["Lead"];
      }
      if (isProductManager) {
        // PM can see Opportunity, Contracts & Payments, and Implementation statuses
        return [...STAGE_STATUSES["Opportunity"], ...STAGE_STATUSES["Contracts & Payments"], ...STAGE_STATUSES["Implementation"]];
      }
      if (canSeeContractsStage) {
        return [...STAGE_STATUSES["Lead"], ...STAGE_STATUSES["Opportunity"], ...STAGE_STATUSES["Contracts & Payments"], ...STAGE_STATUSES["Implementation"]];
      }
      return [...STAGE_STATUSES["Lead"], ...STAGE_STATUSES["Opportunity"], ...STAGE_STATUSES["Implementation"]];
    }
    return STAGE_STATUSES[stageFilter] || [];
  };

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchLeads();
  }, [token, page, searchQuery, stageFilter, sourceFilter, statusFilter, navigate]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "15",
        ...(searchQuery && { search: searchQuery }),
        ...(stageFilter !== "all" && { stage: stageFilter }),
        ...(sourceFilter !== "all" && { source: sourceFilter }),
        ...(statusFilter !== "all" && { status: statusFilter }),
      });

      const response = await fetch(`${API}/admin/leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
        setTotalPages(data.total_pages || 1);
        setPipelineStats(data.pipeline_stats || {});
      }
    } catch (error) {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  // Modal handlers for create/edit
  const handleOpenCreateModal = () => {
    setEditingLead(null);
    setLeadFormMode("create");
    setShowLeadFormModal(true);
  };

  const handleOpenEditModal = (lead) => {
    setEditingLead(lead);
    setLeadFormMode("edit");
    setShowLeadFormModal(true);
  };

  const handleCloseLeadFormModal = () => {
    setShowLeadFormModal(false);
    setEditingLead(null);
    setLeadFormMode("create");
  };

  // Delete handlers
  const handleOpenDeleteModal = (lead) => {
    setLeadToDelete(lead);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setLeadToDelete(null);
  };

  const handleDeleteLead = async () => {
    if (!leadToDelete) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API}/admin/leads/${leadToDelete.lead_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`Lead "${leadToDelete.first_name} ${leadToDelete.last_name}" deleted successfully`);
        handleCloseDeleteModal();
        fetchLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete lead");
      }
    } catch (error) {
      toast.error("Failed to delete lead");
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/status`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Status updated to ${newStatus}`);
        setLeads(prev => prev.map(l => 
          l.lead_id === leadId ? { ...l, status: newStatus } : l
        ));
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Calculate totals
  const totalLeads = (pipelineStats.Lead || 0) + (pipelineStats.Opportunity || 0) + (pipelineStats.Implementation || 0);

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold font-[Manrope] text-white">
                {isSales ? "Lead Management" : isProductManager ? "Opportunity Management" : "Lead Management"}
              </h1>
              <p className="text-zinc-500 mt-1">
                {totalLeads} total {isSales ? "leads" : isProductManager ? "opportunities" : "leads"} in your pipeline
              </p>
            </div>
          </div>
          
          {/* Left side links under header */}
          <div className="flex items-center gap-3 mt-3">
            <button
              onClick={() => navigate("/admin/crm")}
              className="flex items-center gap-2 text-zinc-500 hover:text-[#14A800] transition-colors group"
              data-testid="back-to-crm"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm">Back to CRM Dashboard</span>
            </button>
            
            {/* Add Lead button - hidden for Product Managers */}
            {!isProductManager && (
              <Button 
                onClick={handleOpenCreateModal}
                variant="outline"
                className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                data-testid="add-lead-btn"
              >
                <Target className="w-4 h-4 mr-2" />
                Add Lead
              </Button>
            )}
          </div>
        </div>
        
        {/* Stats Row - Show stats based on role */}
        <div className={`grid grid-cols-2 ${isSales ? 'md:grid-cols-4' : canSeeContractsStage ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4 mb-8`}>
          {/* Lead count - visible to Admin, Sales, Marketing */}
          {!isProductManager && (
            <StatCard icon={Users} value={pipelineStats.Lead || 0} label="Leads" color="#3b82f6" small />
          )}
          {/* Sales sees converted/delivered instead of other stages */}
          {isSales ? (
            <>
              <StatCard icon={TrendingUp} value={pipelineStats.originated_converted || 0} label="Converted" color="#14A800" small />
              <StatCard icon={Target} value={pipelineStats.originated_delivered || 0} label="Delivered" color="#8b5cf6" small />
            </>
          ) : (
            <>
              <StatCard icon={TrendingUp} value={pipelineStats.Opportunity || 0} label="Opportunities" color="#14A800" small />
              {canSeeContractsStage && (
                <StatCard icon={FileText} value={pipelineStats["Contracts & Payments"] || 0} label="Contracts" color="#f59e0b" small />
              )}
              <StatCard icon={Zap} value={pipelineStats.Implementation || 0} label="Implementation" color="#8b5cf6" small />
            </>
          )}
          <StatCard icon={Target} value={totalLeads} label="Total" color="#06b6d4" small />
        </div>
        
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              placeholder="Search by name, email, company..."
              className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#14A800]/50"
              data-testid="search-input"
            />
          </div>
          
          {/* Stage Filter */}
          <Select value={stageFilter} onValueChange={(v) => { setStageFilter(v); setStatusFilter("all"); setPage(1); }}>
            <SelectTrigger className="w-[140px] bg-[#121212] border-white/10 text-white" data-testid="stage-filter">
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all" className="text-white">All Stages</SelectItem>
              {PIPELINE_STAGES.map((stage) => (
                <SelectItem key={stage} value={stage} className="text-white">{stage}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Source Filter */}
          <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[130px] bg-[#121212] border-white/10 text-white" data-testid="source-filter">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all" className="text-white">All Sources</SelectItem>
              <SelectItem value="inbound" className="text-white">Inbound</SelectItem>
              <SelectItem value="outbound" className="text-white">Outbound</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[200px] bg-[#121212] border-white/10 text-white" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 max-h-[400px]">
              <SelectItem value="all" className="text-white">All Status</SelectItem>
              {stageFilter === "all" ? (
                <>
                  {/* Lead statuses - hidden for PM */}
                  {!isProductManager && (
                    <>
                      <SelectItem value="---lead---" disabled className="text-zinc-600 text-xs">── Lead ──</SelectItem>
                      {STAGE_STATUSES["Lead"].map((s) => (
                        <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
                      ))}
                    </>
                  )}
                  <SelectItem value="---opp---" disabled className="text-zinc-600 text-xs">── Opportunity ──</SelectItem>
                  {STAGE_STATUSES["Opportunity"].map((s) => (
                    <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
                  ))}
                  {/* Contracts & Payments - only for Admin and PM */}
                  {canSeeContractsStage && (
                    <>
                      <SelectItem value="---contracts---" disabled className="text-zinc-600 text-xs">── Contracts & Payments ──</SelectItem>
                      {STAGE_STATUSES["Contracts & Payments"].map((s) => (
                        <SelectItem key={`contract-${s}`} value={s} className="text-white">{s}</SelectItem>
                      ))}
                    </>
                  )}
                  <SelectItem value="---impl---" disabled className="text-zinc-600 text-xs">── Implementation ──</SelectItem>
                  {STAGE_STATUSES["Implementation"].map((s) => (
                    <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
                  ))}
                </>
              ) : (
                STAGE_STATUSES[stageFilter]?.map((s) => (
                  <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        
        {/* Table */}
        <div className="rounded-xl border border-white/5 overflow-hidden bg-[#121212]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-[#14A800]" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-20 text-zinc-500">
              <Target className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>No leads found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left px-6 py-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Lead</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Company</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Stage</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Source</th>
                    <th className="text-left px-6 py-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Score</th>
                    <th className="text-right px-6 py-4 text-xs font-medium text-zinc-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {leads.map((lead) => {
                    const leadStage = lead.stage || "Lead";
                    const leadStatus = lead.status || "New";
                    const leadSource = lead.source || "inbound";
                    const priority = lead.priority || { score: 50 };
                    
                    return (
                      <tr 
                        key={lead.lead_id} 
                        className="hover:bg-white/[0.02] transition-colors cursor-pointer"
                        onClick={() => navigate(`/admin/leads/${lead.lead_id}`)}
                        data-testid={`lead-row-${lead.lead_id}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#14A800]/20 to-[#14A800]/5 flex items-center justify-center border border-[#14A800]/20 flex-shrink-0">
                              <span className="text-xs font-bold text-[#14A800]">
                                {(lead.first_name?.[0] || "?")}{(lead.last_name?.[0] || "")}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-white">
                                {lead.first_name} {lead.last_name}
                              </p>
                              <p className="text-xs text-zinc-500 truncate max-w-[200px]">{lead.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-zinc-400">{lead.company_name || "-"}</span>
                        </td>
                        <td className="px-6 py-4">
                          <StageBadge stage={leadStage} />
                        </td>
                        <td className="px-6 py-4">
                          <Select
                            value={leadStatus}
                            onValueChange={(value) => {
                              handleUpdateStatus(lead.lead_id, value);
                            }}
                          >
                            <SelectTrigger 
                              className="w-[140px] h-8 text-xs bg-transparent border-white/10 hover:bg-white/5"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <StatusBadge status={leadStatus} stage={leadStage} />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-white/10">
                              {STAGE_STATUSES[leadStage]?.map((status) => (
                                <SelectItem key={status} value={status} className="text-white text-xs">
                                  {status}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-6 py-4">
                          <SourceBadge source={leadSource} />
                        </td>
                        <td className="px-6 py-4">
                          <PriorityBadge score={priority.score} />
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditModal(lead);
                              }}
                              className="text-zinc-400 hover:text-blue-400 hover:bg-blue-400/10"
                              data-testid={`edit-lead-${lead.lead_id}`}
                              title="Edit Lead"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/admin/leads/${lead.lead_id}`);
                              }}
                              className="text-zinc-400 hover:text-[#14A800] hover:bg-[#14A800]/10"
                              data-testid={`view-lead-${lead.lead_id}`}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDeleteModal(lead);
                              }}
                              className="text-zinc-400 hover:text-red-400 hover:bg-red-400/10"
                              data-testid={`delete-lead-${lead.lead_id}`}
                              title="Delete Lead"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-zinc-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-50"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
      
      {/* Lead Form Modal (Create/Edit) */}
      <LeadFormModal
        isOpen={showLeadFormModal}
        onClose={handleCloseLeadFormModal}
        onSuccess={fetchLeads}
        lead={editingLead}
        mode={leadFormMode}
      />
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && leadToDelete && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" 
          onClick={handleCloseDeleteModal}
          data-testid="delete-lead-modal-overlay"
        >
          <div 
            className="bg-[#0a0a0a] border border-white/10 rounded-xl w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-lead-modal"
          >
            {/* Header */}
            <div className="flex items-center gap-3 p-4 border-b border-white/10">
              <div className="p-2 rounded-lg bg-red-500/10">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white font-[Manrope]">Delete Lead</h2>
                <p className="text-xs text-zinc-500">This action cannot be undone</p>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <p className="text-zinc-300 mb-4">
                Are you sure you want to delete <span className="font-semibold text-white">{leadToDelete.first_name} {leadToDelete.last_name}</span>?
              </p>
              <div className="bg-[#121212] rounded-lg p-3 border border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500/20 to-red-500/5 flex items-center justify-center border border-red-500/20">
                    <span className="text-sm font-bold text-red-400">
                      {leadToDelete.first_name?.[0]}{leadToDelete.last_name?.[0]}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{leadToDelete.first_name} {leadToDelete.last_name}</p>
                    <p className="text-xs text-zinc-500">{leadToDelete.email}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-4 border-t border-white/10">
              <Button
                variant="outline"
                onClick={handleCloseDeleteModal}
                className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10"
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteLead}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-medium"
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Lead
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
